import { INDUSTRIES, type Industry } from '@cyberscore/contracts';
import { Building2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { issueFor } from '../../lib/auth';
import { useSession } from '../../lib/session';
import { useSetUpBusiness } from '../business/onboarding';
import { Alert, AuthLayout, Field, message } from './AuthPages';
import '../business/business.css';
import './auth.css';

/**
 * Signed in, but not part of any organisation yet (decision 0034): set up your business, which starts its
 * Cyber Check, or wait for an invitation to someone else's.
 */
export function NoOrganisation() {
  const { me, signOut, reload } = useSession();
  const setUp = useSetUpBusiness();
  const [name, setName] = useState('');
  const [industry, setIndustry] = useState<Industry | ''>('');
  const [employees, setEmployees] = useState('');
  const [note, setNote] = useState<string | null>(null);
  const error = setUp.error;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!industry) return;
    setUp.mutate({ name, industry, employees: Number(employees), country: 'MY' });
  };
  return (
    <AuthLayout>
      <span className="hicon big">
        <Building2 size={24} />
      </span>
      <h1 style={{ marginTop: 16 }}>Set up your business</h1>
      <p className="lead">Three details and your Cyber Check starts. The questions fit your industry and size.</p>
      <form className="auth-form" onSubmit={submit} noValidate>
        {error != null && !issueFor(error, 'name') && !issueFor(error, 'employees') && <Alert>{message(error)}</Alert>}
        <Field label="Business name" error={issueFor(error, 'name')}>
          {(id) => <input id={id} className="auth-input" value={name} onChange={(e) => setName(e.target.value)} autoComplete="organization" required autoFocus maxLength={200} />}
        </Field>
        <Field label="Industry" error={issueFor(error, 'industry')}>
          {(id) => (
            <select id={id} className="auth-input" value={industry} onChange={(e) => setIndustry(e.target.value as Industry)} required>
              <option value="" disabled>Choose your industry</option>
              {(Object.entries(INDUSTRIES) as [Industry, string][]).map(([k, label]) => (
                <option key={k} value={k}>{label}</option>
              ))}
            </select>
          )}
        </Field>
        <Field label="People working there, including you" error={issueFor(error, 'employees')}>
          {(id) => <input id={id} className="auth-input" type="number" inputMode="numeric" min={1} max={100000} value={employees} onChange={(e) => setEmployees(e.target.value)} required />}
        </Field>
        <p className="muted small">Your business is in Malaysia. You become its owner and administrator, and can invite your team later.</p>
        <button type="submit" className="bbtn pri auth-submit" disabled={setUp.isPending || name.trim().length < 2 || !industry || !(Number(employees) >= 1)}>
          {setUp.isPending ? 'Setting up…' : 'Start my Cyber Check'}
        </button>
      </form>
      {note && <Alert tone="info">{note}</Alert>}
      <p className="auth-alt">
        Invited to someone else’s business? The invitation must be for {me?.user.email}.{' '}
        <button type="button" className="link" onClick={async () => { await reload(); setNote('Still no invitation for this address. Ask the person who invited you to check it.'); }}>
          Check again
        </button>{' '}
        or{' '}
        <button type="button" className="link" onClick={signOut}>sign out</button>.
      </p>
    </AuthLayout>
  ); // prettier-ignore
}
